import { Box, Button, makeStyles } from "@material-ui/core";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import { useEffect, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import { AdviceDelivery, InvestmentOptions } from "../../../../api";
import { Application } from "../../../../common/types";
import { useSatSteps } from "../../../../hooks";
import useApplications from "../../../../hooks/useApplications";
import { useAppDispatch, useAppSelector } from "../../../../store";
import { completeAsync } from "../../../../store/clientQuestionnaires";
import LoadingScreen from "../../../containers/LoadingScreen";
import { Colors, SubtitleText, TitleText } from "../../../controls/helpers";
import { Body, Hero } from "../../GridLayout";
import QuestionnaireScreen from "../../QuestionnaireScreen";
import { usePlanningToWithdraw } from "./BeginQuestions";
import { getAssetAllocationPercentages } from "./SelfSelect";
import { usePlanningToWithdrawWb } from "./WealthBuilder";

const Summary = ({ match }: RouteComponentProps) => {
  const { adviceDelivery, openBrowserApplication, url } = useAppSelector((state) => state.clientQuestionnaires);
  const [buttonsDisabled, setButtonsDisabled] = useState<boolean>(false);
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const clientId = (match.params as { clientId: string }).clientId;
  const applications = useApplications();
  const includeKiwiSaver = applications.includes(Application.KiwiSaver);
  const includeWealthBuilder = applications.includes(Application.WealthBuilder);
  const includeIncomeGenerator = applications.includes(Application.IncomeGenerator);
  const isWithdrawing = usePlanningToWithdraw();
  const isWbWithdrawing = usePlanningToWithdrawWb();
  const ksAllocations = getAssetAllocationPercentages(true);
  const wbAllocations = getAssetAllocationPercentages(false);
  const { goToNextStep } = useSatSteps();
  const [thinkAboutIt, setWillThinkAboutIt] = useState<boolean>();

  const complete = async (willThinkAboutIt?: boolean) => {
    setButtonsDisabled(true);
    setWillThinkAboutIt(willThinkAboutIt);

    const investmentOption = { investmentOption: isWithdrawing ? InvestmentOptions.SelfSelect : InvestmentOptions.LifeCycle };
    const wealthBuilderAssetAllocation = {
      wealthBuilderIncome: wbAllocations.income,
      wealthBuilderInflation: wbAllocations.inflation,
      wealthBuilderGrowth: wbAllocations.growth,
      wealthBuilderInvestmentOption: isWbWithdrawing ? InvestmentOptions.SelfSelect : InvestmentOptions.LifeCycle,
    };
    await dispatch(
      completeAsync({ clientId, includeIncomeGenerator, includeKiwiSaver, includeWealthBuilder, willThinkAboutIt, ...wealthBuilderAssetAllocation, ...investmentOption, ...ksAllocations })
    );
    if (!openBrowserApplication || willThinkAboutIt) goToNextStep(willThinkAboutIt ? "thanks?summary" : undefined);
  };

  useEffect(() => {
    if (adviceDelivery === AdviceDelivery.InPerson) complete();
  }, []);

  useEffect(() => {
    if (url && !thinkAboutIt) window.location.href = url;
  }, [url]);

  return adviceDelivery === AdviceDelivery.InPerson ? (
    <LoadingScreen isNarrowView />
  ) : (
    <QuestionnaireScreen hidePrev hideNext>
      <Hero isNarrowView product="NzFunds" className={classes.hero}>
        <TitleText hideMarginBottom>
          Take action now.
          <br />
          <br />A few minutes today can make a world of difference to your future.
        </TitleText>
      </Hero>
      <Body isNarrowView>
        <Box>
          <TitleText>Next steps</TitleText>
          <Button variant="contained" color="primary" endIcon={<ArrowForwardIcon />} disabled={buttonsDisabled} onClick={(_) => complete()}>
            Sign up now
          </Button>
          <Box mt={2} px={1}>
            <SubtitleText>You'll be able to make your investment selections when onboarding.</SubtitleText>
          </Box>
          <Box mt={5}>
            <Button onClick={(_) => complete(true)} variant="contained" disabled={buttonsDisabled}>
              I'll think about it, email me
            </Button>
          </Box>
        </Box>
      </Body>
    </QuestionnaireScreen>
  );
};

const useStyles = makeStyles(
  {
    hero: {
      alignItems: "normal !important",
      padding: 24,
      "& h4": {
        color: `${Colors.White} !important`,
      },
    },
  },
  { name: "SatSummary" }
);

export default Summary;
